<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            <strong>Google Street View</strong> permite que los clientes vean tu
            negocio antes de visitarlo. Cuando tus espacios aparecen como
            panoramas 360° enlazados en Google Maps y tu ficha de Google Business
            Profile, pueden recorrerlos desde la Búsqueda, Maps o la
            <strong>app Street View</strong>.
          </p>
          <p>
            Con Travvir publicas desde la app, sin tu cuenta Google personal. Las
            imágenes quedan en <em>tu</em> lugar de Google Maps;
            <strong>Travvir es el editor de Street View</strong> en ese sitio.
          </p>
          <p>
            Hay dos caminos. En un <strong>escaneo único</strong>, eliges el
            lugar en Google Maps, seleccionas las escenas y las publicas en Street
            View. En un <strong>tour 360°</strong>, eliges el lugar en Google Maps
            y publicas el tour enlazado directamente.
          </p>
        </section>

        <section>
          <h2>Resumen</h2>
          <ul>
            <li>
              Street View para negocios = panoramas 360° interiores conectados.
            </li>
            <li>
              Travvir es el editor de Street View en tu lugar de Maps — no
              vinculas tu cuenta Google personal en la app.
            </li>
            <li>
              Escaneo único: elige el lugar en Maps, selecciona escenas y
              publica. Tour 360°: elige el lugar y publica el tour directo.
            </li>
            <li>
              Los hotspots de Travvir quedan en tu tour web; en Google solo se
              ven panoramas y flechas de navegación.
            </li>
          </ul>
        </section>

        <section>
          <h2>Cómo funciona hoy</h2>
          <p>
            Primero eliges el <strong>lugar de Google Maps</strong> (nombre y
            pin). Travvir envía los panoramas con su cuenta de editor autorizada.
            En Street View, <strong>Travvir aparece como editor</strong> en ese
            sitio; la cobertura sigue en tu dirección. Google revisa las imágenes
            antes de hacerlas públicas.
          </p>
        </section>

        <section>
          <h2>Publicar con Travvir</h2>
          <p>
            Captura en el móvil y luego Compartir → Google Street View. El mismo
            flujo está en la
            <a :href="`${mainSiteUrl}/google-streetview`"
              >página de Google Street View</a
            >.
          </p>
          <h3>Escaneo único</h3>
          <p>
            Elige el lugar en Google Maps, selecciona las escenas 360° y
            publícalas en Street View. Solo panoramas completos; el pin debe
            coincidir con el edificio correcto.
          </p>
          <h3>Tour 360°</h3>
          <p>
            Elige el lugar en Google Maps y publica el tour enlazado
            directamente, sin subir cada habitación a mano.
          </p>
        </section>

        <section>
          <h2>Requisitos</h2>
          <ul>
            <li>JPEG equirectangular 360° (no imágenes planas).</li>
            <li>Resolución según los mínimos de Google.</li>
            <li>
              Nombre y lugar correctos en Maps. Travvir es el editor de Street
              View en ese sitio.
            </li>
          </ul>
        </section>

        <section>
          <h2>¿App Street View o Travvir?</h2>
          <p>
            La <strong>app Street View</strong> gratuita sirve para una esfera
            rápida. Travvir conviene para varias habitaciones, mejor flujo de
            tour y publicación del recorrido completo en un solo paso, además del
            embed en tu web con hotspots.
          </p>
        </section>

        <section class="continue-reading">
          <h2>Seguir leyendo</h2>
          <ul>
            <li>
              <a :href="`${mainSiteUrl}/google-streetview`"
                >Google Street View con Travvir</a
              >
            </li>
            <li>
              <a href="/blog/es/tour-virtual-google-street-view"
                >Tour virtual de Google Street View</a
              >
            </li>
            <li>
              <a href="/blog/es/guia-google-maps-360">Guía de Google Maps 360</a>
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find(
  (p) => p.slug === "publicar-google-street-view",
);
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;

useBlogSeo({
  title:
    "Google Street View: publicar un tour 360° desde Travvir [2026] | Travvir",
  description:
    "Publica un escaneo o un tour 360° en Google Street View: elige el lugar en Maps; Travvir es el editor en esa ubicación.",
  keywords:
    "google street view, google maps 360, app street view, publicar tour virtual google maps",
  slug: "publicar-google-street-view",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Cómo publicar un escaneo único en Google Street View?",
      answer:
        "Elige el lugar en Google Maps, selecciona las escenas 360° y publícalas. Travvir es el editor de Street View en ese sitio; Google revisa las imágenes antes de hacerlas públicas.",
    },
    {
      question: "¿Cómo publicar un tour 360°?",
      answer:
        "Elige el lugar de Google Maps y publica el tour enlazado directamente desde Travvir. Travvir aparece como editor en ese lugar de Maps.",
    },
  ],
});
</script>
